/**
 * BookEditor page — Phase 3
 * Route: /books/:bookId/editor
 * Auth required. Free tier: Chapter 1 read-only only.
 */

import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import type { BookDocument } from '../types/book';
import { useAuthStore } from '../stores/authStore';
import { BookEditorView } from '../components/book/BookEditorView';
import { useExportBook } from '../services/export';
import { useState } from 'react';
import { Navbar } from '../components/layout/Navbar';

function useBook(bookId: string | undefined) {
  return useQuery({
    queryKey: ['books', bookId],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<BookDocument>>(`/books/${bookId}`);
      return res.data.data ?? null;
    },
    enabled: !!bookId,
    refetchInterval: (query) => {
      if (query.state.data?.status === 'writing') return 5000;
      return false;
    },
  });
}

interface UserProfile {
  subscriptionTier: string | null;
  subscriptionStatus: string | null;
}

function useUserProfile() {
  return useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<UserProfile>>('/users/me');
      // Return null (not undefined) when no user doc exists.
      // RQ v5 throws if queryFn returns undefined; callers already handle null.
      return res.data.data ?? null;
    },
  });
}

export function BookEditor() {
  const { bookId } = useParams<{ bookId: string }>();
  const user = useAuthStore((s) => s.user);
  const { data: book, isLoading: bookLoading } = useBook(bookId);
  const { data: profile } = useUserProfile();
  const exportBook = useExportBook(bookId ?? '');
  const [exportError, setExportError] = useState<string | null>(null);

  const isPaidUser =
    !!profile &&
    profile.subscriptionTier !== null &&
    profile.subscriptionTier !== 'free' &&
    profile.subscriptionStatus === 'active';

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground">
        Please log in to access the editor.
      </div>
    );
  }

  if (bookLoading || !book) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading book…</p>
        </div>
      </div>
    );
  }

  // Redirect back if the book hasn't produced any chapters yet (still in early pipeline stages).
  const canAccess = (book.status === 'complete' || book.status === 'writing') && book.chaptersComplete >= 1;
  if (!canAccess) {
    return <Navigate to={`/books/${bookId}`} replace />;
  }

  const handleExport = async (format: 'epub' | 'docx') => {
    setExportError(null);
    try {
      const result = await exportBook.mutateAsync(format);
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Export failed. Please try again.';
      if (message.includes('FREE_TIER') || (err as { response?: { status?: number } })?.response?.status === 403) {
        setExportError('Export is available on paid plans. Upgrade to download your book.');
      } else {
        setExportError('Export failed. Please try again.');
      }
    }
  };

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      {/* Global site nav */}
      <Navbar variant="authed" />

      {/* Editor-specific sub-header: book context + export actions */}
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-card px-4 py-2">
        <div className="flex items-center gap-3">
          <Link
            to={`/books/${bookId}`}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back
          </Link>
          <span className="text-border">|</span>
          <h1 className="text-sm font-medium text-foreground truncate max-w-xs">{book.title}</h1>
          {book.chaptersComplete > 0 && (
            <span className="text-xs text-muted-foreground">
              {isPaidUser
                ? `${book.chaptersComplete}/${book.chaptersTotal}`
                : `${Math.min(book.chaptersComplete, 1)}/1`} chapters
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isPaidUser ? (
            <>
              <button
                type="button"
                onClick={() => void handleExport('epub')}
                disabled={exportBook.isPending}
                className="rounded border border-border bg-secondary px-3 py-1.5 text-xs text-foreground hover:bg-accent transition-colors disabled:opacity-50"
              >
                {exportBook.isPending ? 'Exporting…' : 'Export EPUB'}
              </button>
              <button
                type="button"
                onClick={() => void handleExport('docx')}
                disabled={exportBook.isPending}
                className="rounded border border-border bg-secondary px-3 py-1.5 text-xs text-foreground hover:bg-accent transition-colors disabled:opacity-50"
              >
                {exportBook.isPending ? 'Exporting…' : 'Export DOCX'}
              </button>
            </>
          ) : (
            <Link
              to="/pricing"
              className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Upgrade to Export
            </Link>
          )}
        </div>
      </div>

      {/* Export error banner */}
      {exportError && (
        <div className="shrink-0 border-b border-destructive/30 bg-destructive/10 px-4 py-2">
          <p className="text-xs text-destructive">{exportError}</p>
        </div>
      )}

      {/* Editor body */}
      <main className="flex-1 overflow-hidden">
        <BookEditorView
          book={book}
          isPaidUser={isPaidUser}
        />
      </main>
    </div>
  );
}
